const SCRIPT_ATTR = "data-vf-klaviyo";

export const ensureKlaviyoQueue = () => {
  if (typeof window === "undefined") return;
  // Klaviyo bootstrap proxy (matches the snippet shipped with onsite/js/klaviyo.js)
  const w = window as any;
  if (w.klaviyo) return;
  w._klOnsite = w._klOnsite || [];
  try {
    w.klaviyo = new Proxy(
      {},
      {
        get(_t, i: string) {
          if (i === "push") {
            return function () {
              w._klOnsite.push.apply(w._klOnsite, arguments as any);
            };
          }
          return function () {
            const argsArr: any[] = [];
            for (let x = 0; x < arguments.length; x++) {
              argsArr.push(arguments[x]);
            }
            const cb =
              typeof argsArr[argsArr.length - 1] === "function"
                ? argsArr.pop()
                : undefined;
            return new Promise((resolve) => {
              w._klOnsite.push([
                i,
                ...argsArr,
                (res: any) => {
                  if (cb) cb(res);
                  resolve(res);
                },
              ]);
            });
          };
        },
      }
    );
  } catch (e) {
    w.klaviyo = w.klaviyo || [];
    w.klaviyo.push = function () {
      w._klOnsite.push.apply(w._klOnsite, arguments as any);
    };
  }
};

// Keep automatic forms, but don't start their runtime from the first tap or
// scroll while hydration and product images are still busy. A bounded fallback
// also allows forms to load when an unrelated resource stalls window.load.
export const waitForOnsiteLoad = (): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }

    let done = false;
    let idleId: number | undefined;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener("load", afterLoad);
      clearTimeout(deadline);
      clearTimeout(settleTimer);
      if (idleId !== undefined) (window as any).cancelIdleCallback?.(idleId);
      resolve();
    };
    const afterLoad = () => {
      if (done) return;
      settleTimer = setTimeout(() => {
        if (typeof (window as any).requestIdleCallback === "function") {
          idleId = (window as any).requestIdleCallback(finish, {
            timeout: 2000,
          });
        } else finish();
      }, 1500);
    };
    const deadline = setTimeout(finish, 8000);
    if (document.readyState === "complete") {
      afterLoad();
    } else {
      window.addEventListener("load", afterLoad, { once: true });
    }
  });
};

const scriptLoads = new Map<string, Promise<boolean>>();

export const loadOnsiteScript = (companyId: string): Promise<boolean> => {
  if (typeof document === "undefined" || !companyId)
    return Promise.resolve(false);
  const pending = scriptLoads.get(companyId);
  if (pending) return pending;
  const result = new Promise<boolean>((resolve) => {
    // Recognize both supported snippet URLs, including a tag inserted by GTM.
    const existing = Array.from(document.scripts).find((script) => {
      try {
        const url = new URL(script.src);
        return (
          url.hostname === "static.klaviyo.com" &&
          ((url.pathname === "/onsite/js/klaviyo.js" &&
            url.searchParams.get("company_id") === companyId) ||
            url.pathname ===
              `/onsite/js/${encodeURIComponent(companyId)}/klaviyo.js`)
        );
      } catch {
        return false;
      }
    });
    if (existing?.getAttribute("data-vf-klaviyo-state") === "loaded") {
      resolve(true);
      return;
    }
    const s = existing || document.createElement("script");
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      s.removeEventListener("load", loaded);
      s.removeEventListener("error", failed);
      s.setAttribute("data-vf-klaviyo-state", ok ? "loaded" : "failed");
      if (!ok && !existing) s.remove();
      resolve(ok);
    };
    const loaded = () => finish(true);
    const failed = () => finish(false);
    const timeout = setTimeout(failed, 30000);
    s.addEventListener("load", loaded, { once: true });
    s.addEventListener("error", failed, { once: true });
    if (existing) {
      // A previously loaded external tag may have already fired load. This
      // documented read-only callback proves the SDK is processing its queue.
      (window as any).klaviyo?.push(["isIdentified", () => finish(true)]);
    }
    if (!existing) {
      s.async = true;
      s.type = "text/javascript";
      s.src = `https://static.klaviyo.com/onsite/js/${encodeURIComponent(
        companyId
      )}/klaviyo.js`;
      s.setAttribute(SCRIPT_ATTR, companyId);
      s.setAttribute("data-vf-klaviyo-state", "loading");
      document.head.appendChild(s);
    }
  });
  scriptLoads.set(companyId, result);
  void result.then((ok) => {
    if (!ok) scriptLoads.delete(companyId);
  });
  return result;
};
